import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import type { BloxCustomDataProviderType } from 'utility/types/dataProvider';

export type StoryTemplate = {
  uuid: string;
  title: string;
  type?: string;
};

export type UseStoryTemplatesOptions = {
  assetType?: string;
  enabled?: boolean;
};

export type UseStoryTemplatesResult = {
  templates: StoryTemplate[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  fetchOnOpen: () => void;
  hasFetched: boolean;
};

/**
 * Hook to fetch story templates for a given asset type.
 * Templates are fetched lazily when the user opens the dropdown (via fetchOnOpen).
 * This avoids unnecessary network requests when the modal opens.
 */
export const useStoryTemplates = (
  options: UseStoryTemplatesOptions = {},
): UseStoryTemplatesResult => {
  const { assetType = 'article', enabled = true } = options;
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const [hasFetched, setHasFetched] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  const query = useQuery<StoryTemplate[], Error>({
    queryKey: ['storyTemplates', assetType],
    queryFn: async () => {
      const result = await dataProvider.getList('editorial', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'title', order: 'ASC' },
        filter: {
          template: 'yes',
          type: [assetType],
        },
      });

      const templates: StoryTemplate[] = (result?.data ?? []).map((item: any) => ({
        uuid: item.uuid,
        title: item.title || 'Untitled Template',
        type: item.type,
      }));

      return templates;
    },
    enabled: enabled && shouldFetch,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    retry: 1,
  });

  const fetchOnOpen = useCallback(() => {
    if (!hasFetched) {
      setHasFetched(true);
      setShouldFetch(true);
    }
  }, [hasFetched]);

  return {
    templates: query.data ?? [],
    isLoading: query.isLoading || query.isFetching,
    isError: query.isError,
    error: query.error ?? null,
    refetch: query.refetch,
    fetchOnOpen,
    hasFetched,
  };
};
